import type { LucideIcon } from "lucide-react";
import {
  Banknote,
  Building2,
  Car,
  CircleEllipsis,
  Expand,
  FileClock,
  FileText,
  Hammer,
  HandCoins,
  Handshake,
  HousePlus,
  Landmark,
  LandPlot,
  Package,
  RefreshCw,
} from "lucide-react";
import { getI18n } from "react-i18next";

import { LoanApplicationReasonEnum } from "@krea/shared/types/loanApplication";

const i18n = getI18n();

export interface IReasonOption {
  value: LoanApplicationReasonEnum;
  readonly name: string;
  readonly description?: string;
  icon?: LucideIcon;
}

export interface IReasonCategory {
  id: string;
  labelKey: string;
  reasons: IReasonOption[];
}

export const REAL_ESTATE_REASONS: IReasonOption[] = [
  {
    value: LoanApplicationReasonEnum.BRIDGE_FINANCING_SHORTER_PAYMENT_TIME,
    get name() {
      return i18n.t("applicationForm.commons.reasons.bridge_financing.title");
    },
    get description() {
      return i18n.t(
        "applicationForm.commons.reasons.bridge_financing.description",
      );
    },
    icon: FileClock,
  },
  {
    value: LoanApplicationReasonEnum.PROJECT,
    get name() {
      return i18n.t("applicationForm.commons.reasons.project_financing.title");
    },
    get description() {
      return i18n.t(
        "applicationForm.commons.reasons.project_financing.description",
      );
    },
    icon: Landmark,
  },
  {
    value: LoanApplicationReasonEnum.PURCHASE_REAL_ESTATE,
    get name() {
      return i18n.t(
        "applicationForm.commons.reasons.purchase_real_estate.title",
      );
    },
    get description() {
      return i18n.t(
        "applicationForm.commons.reasons.purchase_real_estate.description",
      );
    },
    icon: HousePlus,
  },
  {
    value: LoanApplicationReasonEnum.REFINANCE_CURRENT_PROPERTY_LOAN,
    get name() {
      return i18n.t(
        "applicationForm.commons.reasons.refinance_real_estate_loan.title",
      );
    },
    get description() {
      return i18n.t(
        "applicationForm.commons.reasons.refinance_real_estate_loan.description",
      );
    },
    icon: RefreshCw,
  },
  {
    value: LoanApplicationReasonEnum.REAL_ESTATE_NEW_CONSTRUCTION_OR_RENOVATION,
    get name() {
      return i18n.t(
        "applicationForm.commons.reasons.renovation_of_property.title",
      );
    },
    get description() {
      return i18n.t(
        "applicationForm.commons.reasons.renovation_of_property.description",
      );
    },
    icon: Hammer,
  },
  {
    value: LoanApplicationReasonEnum.OTHER_PROPERTY_FINANCE,
    get name() {
      return i18n.t(
        "applicationForm.commons.reasons.other_property_finance.title",
      );
    },
    get description() {
      return i18n.t(
        "applicationForm.commons.reasons.other_property_finance.description",
      );
    },
    icon: LandPlot,
  },
];

const REAL_ESTATE_VALUES = new Set<string>(
  REAL_ESTATE_REASONS.map((r) => r.value),
);

export const isRealEstateReason = (
  reason: LoanApplicationReasonEnum | string,
): boolean => REAL_ESTATE_VALUES.has(reason);

/** Sentinel value for primary purpose form: selecting "Real Estate" navigates to Real Estate substep. */
export const REAL_ESTATE_SENTINEL = "REAL_ESTATE";

export interface IPrimaryPurposeReasonOption
  extends Omit<IReasonOption, "value"> {
  value: LoanApplicationReasonEnum | typeof REAL_ESTATE_SENTINEL;
}

export interface IPrimaryPurposeCategory {
  id: string;
  labelKey: string;
  reasons: IPrimaryPurposeReasonOption[];
}

const LABEL_KEYS = {
  investments: "applicationForm.commons.reasonCategories.investments",
  workingCapital: "applicationForm.commons.reasonCategories.working_capital",
  realEstate: "applicationForm.commons.reasonCategories.real_estate",
  other: "applicationForm.commons.reasonCategories.other",
} as const;

const INVESTMENTS_REASONS: IReasonOption[] = [
  {
    value: LoanApplicationReasonEnum.OBJECT_FINANCING,
    get name() {
      return i18n.t("applicationForm.commons.reasons.object_financing.title");
    },
    get description() {
      return i18n.t(
        "applicationForm.commons.reasons.object_financing.description",
      );
    },
    icon: Car,
  },
  {
    value: LoanApplicationReasonEnum.SUPPLY,
    get name() {
      return i18n.t("applicationForm.commons.reasons.supply.title");
    },
    get description() {
      return i18n.t("applicationForm.commons.reasons.supply.description");
    },
    icon: Package,
  },
  {
    value: LoanApplicationReasonEnum.PURCHASE,
    get name() {
      return i18n.t(
        "applicationForm.commons.reasons.acquisition_financing.title",
      );
    },
    get description() {
      return i18n.t(
        "applicationForm.commons.reasons.acquisition_financing.description",
      );
    },
    icon: Handshake,
  },
  {
    value: LoanApplicationReasonEnum.OTHER_EXPANSION,
    get name() {
      return i18n.t("applicationForm.commons.reasons.expansion.title");
    },
    get description() {
      return i18n.t("applicationForm.commons.reasons.expansion.description");
    },
    icon: Expand,
  },
];

const WORKING_CAPITAL_REASONS: IReasonOption[] = [
  {
    value: LoanApplicationReasonEnum.INVOICE_FINANCING,
    get name() {
      return i18n.t("applicationForm.commons.reasons.invoice_financing.title");
    },
    get description() {
      return i18n.t(
        "applicationForm.commons.reasons.invoice_financing.description",
      );
    },
    icon: FileText,
  },
  {
    value: LoanApplicationReasonEnum.OTHER_LIQUIDITY_NEEDS,
    get name() {
      return i18n.t(
        "applicationForm.commons.reasons.short_term_liquidity.title",
      );
    },
    get description() {
      return i18n.t(
        "applicationForm.commons.reasons.short_term_liquidity.description",
      );
    },
    icon: Banknote,
  },
];

const OTHER_REASONS: IReasonOption[] = [
  {
    value: LoanApplicationReasonEnum.DEBT,
    get name() {
      return i18n.t("applicationForm.commons.reasons.refinancing.title");
    },
    get description() {
      return i18n.t("applicationForm.commons.reasons.refinancing.description");
    },
    icon: HandCoins,
  },
  {
    value: LoanApplicationReasonEnum.OTHER,
    get name() {
      return i18n.t("applicationForm.commons.reasons.other.title");
    },
    get description() {
      return i18n.t("applicationForm.commons.reasons.other.description");
    },
    icon: CircleEllipsis,
  },
];

const REAL_ESTATE_SENTINEL_OPTION: IPrimaryPurposeReasonOption = {
  value: REAL_ESTATE_SENTINEL,
  get name() {
    return i18n.t("applicationForm.commons.reasonCategories.real_estate");
  },
  get description() {
    return i18n.t(
      "applicationForm.commons.reasonCategories.real_estate_description",
    );
  },
  icon: Building2,
};

/** Flat list of all primary purpose options for desktop grid (no category grouping). */
export const PRIMARY_PURPOSE_REASONS: IPrimaryPurposeReasonOption[] = [
  ...INVESTMENTS_REASONS,
  ...WORKING_CAPITAL_REASONS,
  REAL_ESTATE_SENTINEL_OPTION,
  ...OTHER_REASONS,
];

/** Grouped by category for mobile view. */
export const PRIMARY_PURPOSE_CATEGORIES: IPrimaryPurposeCategory[] = [
  {
    id: "investments",
    labelKey: LABEL_KEYS.investments,
    reasons: INVESTMENTS_REASONS,
  },
  {
    id: "working_capital",
    labelKey: LABEL_KEYS.workingCapital,
    reasons: WORKING_CAPITAL_REASONS,
  },
  {
    id: "real_estate",
    labelKey: LABEL_KEYS.realEstate,
    reasons: [REAL_ESTATE_SENTINEL_OPTION],
  },
  { id: "other", labelKey: LABEL_KEYS.other, reasons: OTHER_REASONS },
];

export const REASON_CATEGORIES: IReasonCategory[] = [
  {
    id: "investments",
    labelKey: LABEL_KEYS.investments,
    reasons: INVESTMENTS_REASONS,
  },
  {
    id: "working_capital",
    labelKey: LABEL_KEYS.workingCapital,
    reasons: WORKING_CAPITAL_REASONS,
  },
  {
    id: "real_estate",
    labelKey: LABEL_KEYS.realEstate,
    reasons: REAL_ESTATE_REASONS,
  },
  { id: "other", labelKey: LABEL_KEYS.other, reasons: OTHER_REASONS },
];

export const PRIMARY_REASONS: IReasonOption[] = REASON_CATEGORIES.flatMap(
  (c) => c.reasons,
);

export const REASONS: IReasonOption[] = [
  ...REASON_CATEGORIES.flatMap((c) =>
    c.id === "real_estate" ? [] : c.reasons,
  ),
  ...REAL_ESTATE_REASONS,
];
